import { lazy } from 'react'
import { Redirect, RouteProps } from 'react-router-dom'

import AuthenticationHandler from './components/AuthenticationHandler'
import { Role } from './components/RolesContainer'
import HomePage from './pages/HomePage'
import OrganizationAuditConnectorPage from './pages/OrganizationAuditConnectorPage'
import OrganizationAuditPage from './pages/OrganizationAuditPage'
import OrganizationChannelConnectionsPage from './pages/OrganizationChannelConnectionsPage'
import OrganizationClientConfigsPage from './pages/OrganizationClientConfigsPage'
import OrganizationGroupsMembersPage from './pages/OrganizationGroupsMembersPage'
import OrganizationGroupsPage from './pages/OrganizationGroupsPage'
import OrganizationSecurityPage from './pages/OrganizationSecurityPage'
import OrganizationSettingsPage from './pages/OrganizationSettingsPage'
import OrganizationSpaceEditPage from './pages/OrganizationSpaceEditPage'
import OrganizationSpacesPage from './pages/OrganizationSpacesPage'
import OrganizationToolbarsPage from './pages/OrganizationToolbarsPage'
import OrganizationUserAttributeCreatePage from './pages/OrganizationUserAttributeCreatePage'
import OrganizationUserAttributeEditPage from './pages/OrganizationUserAttributeEditPage'
import OrganizationUserAttributesPage from './pages/OrganizationUserAttributesPage'
import OrganizationUserProvisioningPage from './pages/OrganizationUserProvisioningPage'
import OrganizationUserBulkAddPage from './pages/OrganizationUsersBulkAddPage'
import OrganizationUsersPage from './pages/OrganizationUsersPage'
import SpaceAnalyticsPage from './pages/SpaceAnalyticsPage'
import SpaceAppBuilderAnalyticsPage from './pages/SpaceAppBuilder/AnalyticsPage/AnalyticsPage'
import SpaceAppBuilderDataSourcesBuilderPage from './pages/SpaceAppBuilder/DataSourcesBuilderPage'
import SpaceAppBuilderDataSourcesPage from './pages/SpaceAppBuilder/DataSourcesPage'
import SpaceAppBuilderFeaturesHistoryPage from './pages/SpaceAppBuilder/HistoryPage'
import SpaceAppBuilderLogsPage from './pages/SpaceAppBuilder/LogsPage'
import SpaceAppBuilderWorkgridDataSourceDetailsPage from './pages/SpaceAppBuilder/WorkgridDataSourceDetailsPage'
import SpaceCatalogAppDetailsPage from './pages/SpaceCatalogAppDetailsPage'
import SpaceConnectorLogsPage from './pages/SpaceConnectorLogsPage'
import SpaceCustomAppEditPage from './pages/SpaceCustomAppEditPage'
import SpaceMemberEditPage from './pages/SpaceMemberEditPage'
import SpaceMembersPage from './pages/SpaceMembersPage'
import SpaceMembersRolesPage from './pages/SpaceMembersRolesPage'
import SpaceNotificationSourcesPage from './pages/SpaceNotificationSourcesPage'
import SpaceNotificatorPage from './pages/SpaceNotificatorPage'
import SpacePlaygroundPage from './pages/SpacePlaygroundPage'
import SpacePublisherClonePage from './pages/SpacePublisherClonePage'
import SpacePublisherEditPage from './pages/SpacePublisherEditPage'
import SpacePublisherPage from './pages/SpacePublisherPage'

/*
 * Code splitting allows us to intelligently control what source code our clients download based on the pages they
 * choose to interact with. Webpack will generate separate bundles for each of these pages that React will download as
 * it needs them based on what page a user navigates to. This will significantly reduce the main webpack bundles size!
 * See https://reactjs.org/docs/code-splitting.html for more information.
 */

const SpaceAppCatalogV3Page = lazy(() => import('./pages/SpaceAppCatalogV3Page'))
const SpaceAppCatalogV3CatalogItemPage = lazy(() => import('./pages/SpaceAppCatalogV3CatalogItemPage'))
const SpaceAppsV3InstalledPage = lazy(() => import('./pages/SpaceAppsV3InstalledPage'))

// uses the `recharts` module which adds .25 MB to the main webpack bundle
const ChatbotAnalyticsPage = lazy(() => import('./pages/SpaceChatbotAnalyticsPage'))
const OrganizationUserEditPage = lazy(() => import('./pages/OrganizationUserEditPage'))
const KnowledgeHubBuilderPage = lazy(() => import('./pages/SpaceChatbotKnowledgeHubBuilderPage'))
const CorporateDictionaryPage = lazy(() => import('./pages/SpaceChatbotCorporateDictionaryPage'))
const CorporateDictionaryBulkPage = lazy(() => import('./pages/SpaceChatbotCorporateDictionaryBulkAddPage'))
const SpaceChatbotPage = lazy(() => import('./pages/SpaceChatbotPage'))
const SpaceChatbotLogsPage = lazy(() => import('./pages/SpaceChatbotLogsPage'))
const SpaceChatbotTestPage = lazy(() => import('./pages/SpaceChatbotTestPage'))
// use the `@workgrid/client-core` and `@workgrid/client-component` modules which add 0.7 MB to the main webpack bundle
const SpacePublisherBuilderPage = lazy(() => import('./pages/SpacePublisherBuilderPage'))

const SpaceKnowledgePage = lazy(() => import('./pages/SpaceKnowledgePage/LandingPage'))
const SpaceKnowledgeQnASummaryPage = lazy(() => import('./pages/SpaceKnowledgePage/QnASummaryPage'))
const SpaceKnowledgeQnADetailsPage = lazy(() => import('./pages/SpaceKnowledgePage/QnADetailsPage'))

const SpacePublisherLogsPage = lazy(() => import('./pages/SpacePublisherLogsPage'))

const SpaceAppBuilderPage = lazy(() => import('./pages/SpaceAppBuilder/LandingPage'))
const SpaceAppBuilderCreateAppPage = lazy(() => import('./pages/SpaceAppBuilder/TemplatesPage'))
const SpaceAppBuilderDetailsPage = lazy(() => import('./pages/SpaceAppBuilder/AppDetailsPage'))
const SpaceAppBuilderFeaturesPage = lazy(() => import('./pages/SpaceAppBuilder/FeaturesPage'))
const SpaceAppBuilderFeaturesHistoryDetailsPage = lazy(() => import('./pages/SpaceAppBuilder/HistoryDetailsPage'))

const SpaceAppBuilderTemplateDetailPage = lazy(() => import('./pages/SpaceAppBuilder/TemplateDetailPage'))
const SpaceAppBuilderTemplatePreviewPage = lazy(() => import('./pages/SpaceAppBuilder/AppDetailsPage/TemplatePreview'))
const SpaceAppBuilderImportTemplatePage = lazy(() => import('./pages/SpaceAppBuilder/TemplateImportPage'))

type AppProps = {
  path: string // path is required, and must be a string
  requiresAuthentication?: boolean
  requiresRoles?: Role[]
}
export type AppRoute = RouteProps & AppProps

export const pageRoutes = {
  OrganizationSettingsPage: {
    path: '/organization',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationSecurityPage: {
    path: '/organization/security',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationAuditPage: {
    path: '/organization/audit',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationAuditConnectorPage: {
    path: '/organization/audit/api',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationClientConfigsPage: {
    path: '/organization/client-configs',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationSpacesPage: {
    path: '/organization/spaces',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationSpaceCreatePage: {
    path: '/organization/spaces/create',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationSpaceEditPage: {
    path: '/organization/spaces/:spaceId',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  SpacePlaygroundPage: {
    path: '/space/:spaceId/playground',
    requiresAuthentication: true
  },
  SpaceNotificatorPage: {
    path: '/space/:spaceId/notificator',
    requiresAuthentication: true
  },
  OrganizationToolbarsPage: {
    path: '/organization/toolbars',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationChannelConnectionsPage: {
    path: '/organization/channelconnections',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationUsersPage: {
    path: '/organization/users',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationUserCreatePage: {
    path: '/organization/users/create',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationUserEditPage: {
    path: '/organization/users/:userId/edit',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationUserBulkAddPage: {
    path: '/organization/users/bulk',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationUserAttributesPage: {
    path: '/organization/users/attributes',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationUserAttributeCreatePage: {
    path: '/organization/users/attributes/create',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationUserAttributeEditPage: {
    path: '/organization/users/attributes/:attributeId/edit',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationGroupsPage: {
    path: '/organization/groups',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationGroupsMembersPage: {
    path: '/organization/groups/:groupId/members',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  OrganizationUserProvisioningPage: {
    path: '/organization/users/provisioning',
    requiresAuthentication: true,
    requiresRoles: [Role.ORGANIZATION_ADMIN_ROLE]
  },
  SpaceNotificationSourcesPage: {
    path: '/space/:spaceId/notificationsources',
    requiresAuthentication: true,
    requiresRoles: [
      Role.SPACE_ADMIN_ROLE,
      Role.PUBLISHER_ADMIN_ROLE,
      Role.PUBLISHER_ROLE,
      Role.WORKSHOP_MANAGER_ROLE,
      Role.WORKSHOP_APP_OWNER
    ]
  },
  SpacePublisherPage: {
    path: '/space/:spaceId/publisher',
    requiresAuthentication: true,
    requiresRoles: [Role.PUBLISHER_ADMIN_ROLE, Role.PUBLISHER_ROLE]
  },
  SpacePublisherBuilderPage: {
    path: '/space/:spaceId/publisher/builder',
    requiresAuthentication: true,
    requiresRoles: [Role.PUBLISHER_ADMIN_ROLE, Role.PUBLISHER_ROLE]
  },
  SpacePublisherEditPage: {
    path: '/space/:spaceId/publisher/builder/:notificationId',
    requiresAuthentication: true,
    requiresRoles: [Role.PUBLISHER_ADMIN_ROLE, Role.PUBLISHER_ROLE]
  },
  SpacePublisherClonePage: {
    path: '/space/:spaceId/publisher/:notificationId/clone',
    requiresAuthentication: true,
    requiresRoles: [Role.PUBLISHER_ADMIN_ROLE, Role.PUBLISHER_ROLE]
  },
  SpacePublisherLogsPage: {
    path: '/space/:spaceId/publisher/:notificationId/logs',
    requiresAuthentication: true,
    requiresRoles: [Role.PUBLISHER_ADMIN_ROLE, Role.PUBLISHER_ROLE]
  },
  SpaceAnalyticsPage: {
    path: '/space/:spaceId/analytics',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  SpaceAppsRedirect: {
    path: '/space/:spaceId/apps',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  SpaceAppsV3InstalledPage: {
    path: '/space/:spaceId/apps/installed',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  SpaceCustomAppPage: {
    path: '/space/:spaceId/apps/customapp',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  SpaceCustomAppEditPage: {
    path: '/space/:spaceId/apps/customapp/:appId/edit',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  SpaceAppCatalogV3Page: {
    path: '/space/:spaceId/apps/catalog',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  SpaceCatalogAppDetailsEditPage: {
    path: '/space/:spaceId/apps/catalog/:appId/edit',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  SpaceCatalogAppDetailsInstallPage: {
    path: '/space/:spaceId/apps/catalog/:appId/install',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  SpaceAppCatalogV3CatalogItemInstallPage: {
    path: '/space/:spaceId/apps/v3/catalog/:catalogItemId/install',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  SpaceAppCatalogV3CatalogItemEditPage: {
    path: '/space/:spaceId/apps/v3/catalog/:appId/edit',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  SpaceAppCatalogV3CatalogItemPage: {
    path: '/space/:spaceId/apps/catalog/:catalogItemId',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  SpaceConnectorLogsPage: {
    path: '/space/:spaceId/apps/:appId/:connectorId/logs',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  SpaceAppBuilderPage: {
    path: '/space/:spaceId/appbuilder',
    requiresAuthentication: true,
    requiresRoles: [Role.WORKSHOP_MANAGER_ROLE, Role.WORKSHOP_APP_OWNER]
  },
  SpaceAppBuilderCreateAppPage: {
    path: '/space/:spaceId/appbuilder/templates',
    requiresAuthentication: true
  },
  SpaceAppBuilderTemplateDetailPage: {
    path: '/space/:spaceId/appbuilder/templates/:templateId',
    requiresAuthentication: true
  },
  SpaceAppBuilderTemplatePreviewPage: {
    path: '/space/:spaceId/appbuilder/templates/:templateId/preview',
    requiresAuthentication: true
  },
  SpaceAppBuilderImportTemplatePage: {
    path: '/space/:spaceId/appbuilder/templates/:templateId/import',
    requiresAuthentication: true,
    requiresRoles: [Role.WORKSHOP_MANAGER_ROLE, Role.WORKSHOP_APP_OWNER]
  },
  SpaceAppBuilderPreviewFeaturesPage: {
    path: '/space/:spaceId/appbuilder/templates/:templateId/preview/:featureId',
    requiresAuthentication: true
  },
  SpaceAppBuilderDataSourcesPage: {
    path: '/space/:spaceId/appbuilder/datasources',
    requiresAuthentication: true,
    requiresRoles: [Role.WORKSHOP_MANAGER_ROLE, Role.WORKSHOP_APP_OWNER]
  },
  SpaceAppBuilderDataSourcesBuilderPage: {
    path: '/space/:spaceId/appbuilder/datasources/builder',
    requiresAuthentication: true,
    requiresRoles: [Role.WORKSHOP_MANAGER_ROLE, Role.WORKSHOP_APP_OWNER]
  },
  SpaceAppBuilderWorkgridDataSourceDetailsPage: {
    path: '/space/:spaceId/appbuilder/datasources/:datasourceId/instances/:datasourceInstanceId',
    requiresAuthentication: true,
    requiresRoles: [Role.WORKSHOP_MANAGER_ROLE, Role.WORKSHOP_APP_OWNER]
  },
  SpaceKnowledgePage: {
    path: '/space/:spaceId/knowledge',
    requiresAuthentication: true,
    requiresRoles: [Role.WORKSHOP_MANAGER_ROLE, Role.WORKSHOP_APP_OWNER]
  },
  SpaceKnowledgeDocumentsPage: {
    path: '/space/:spaceId/knowledge/documents',
    requiresAuthentication: true,
    requiresRoles: [Role.WORKSHOP_MANAGER_ROLE, Role.WORKSHOP_APP_OWNER]
  },
  SpaceKnowledgeDocumentEditPage: {
    path: '/space/:spaceId/knowledge/documents/:knowledgeId',
    requiresAuthentication: true,
    requiresRoles: [Role.WORKSHOP_MANAGER_ROLE, Role.WORKSHOP_APP_OWNER]
  },
  SpaceKnowledgeQnASummaryPage: {
    path: '/space/:spaceId/knowledge/qa',
    requiresAuthentication: true,
    requiresRoles: [Role.CHATBOT_ADMIN_ROLE]
  },
  SpaceKnowledgeQnADetailsPage: {
    path: '/space/:spaceId/knowledge/qa/details',
    requiresAuthentication: true,
    requiresRoles: [Role.CHATBOT_ADMIN_ROLE]
  },
  SpaceKnowledgeQnADetailsEditPage: {
    path: '/space/:spaceId/knowledge/qa/details/:questionSetId',
    requiresAuthentication: true,
    requiresRoles: [Role.CHATBOT_ADMIN_ROLE]
  },
  SpaceAppBuilderDetailsPage: {
    path: '/space/:spaceId/appbuilder/:appId',
    requiresAuthentication: true,
    requiresRoles: [Role.WORKSHOP_MANAGER_ROLE, Role.WORKSHOP_APP_OWNER]
  },
  SpaceAppBuilderFeaturesPage: {
    path: '/space/:spaceId/appbuilder/:appId/features/:featureId',
    requiresAuthentication: true,
    requiresRoles: [Role.WORKSHOP_MANAGER_ROLE, Role.WORKSHOP_APP_OWNER]
  },
  SpaceAppBuilderFeaturesHistoryPage: {
    path: '/space/:spaceId/appbuilder/:appId/features/:featureId/featureHistory',
    requiresAuthentication: true,
    requiresRoles: [Role.WORKSHOP_APP_OWNER]
  },
  SpaceAppBuilderFeaturesHistoryDetailsPage: {
    path: '/space/:spaceId/appbuilder/:appId/features/:featureId/featureHistory/:executionHistoryId',
    requiresAuthentication: true,
    requiresRoles: [Role.WORKSHOP_APP_OWNER]
  },
  SpaceAppBuilderFeaturesHistoryDetailsByDatePage: {
    path: '/space/:spaceId/appbuilder/:appId/features/:featureId/featureHistory/:executionHistoryId/:executionDate',
    requiresAuthentication: true,
    requiresRoles: [Role.WORKSHOP_APP_OWNER]
  },
  SpaceAppBuilderAnalyticsPage: {
    path: '/space/:spaceId/appbuilder/:appId/features/:featureId/featureHistory/:executionHistoryId/analytics/:correlationId',
    requiresAuthentication: true,
    requiresRoles: [Role.WORKSHOP_APP_OWNER]
  },
  SpaceAppBuilderLogsPage: {
    path: '/space/:spaceId/appbuilder/:appId/logs',
    requiresAuthentication: true,
    requiresRoles: [Role.WORKSHOP_MANAGER_ROLE, Role.WORKSHOP_APP_OWNER]
  },

  SpaceChatbotPage: {
    path: '/space/:spaceId/chatbot',
    requiresAuthentication: true,
    requiresRoles: [Role.CHATBOT_ADMIN_ROLE]
  },
  SpaceChatbotLogsPage: {
    path: '/space/:spaceId/chatbot/knowledgehub/:questionSetId/logs',
    requiresAuthentication: true,
    requiresRoles: [Role.CHATBOT_ADMIN_ROLE]
  },
  KnowledgeHubBuilderPage: {
    path: '/space/:spaceId/chatbot/knowledgehub/builder',
    requiresAuthentication: true,
    requiresRoles: [Role.CHATBOT_ADMIN_ROLE]
  },
  KnowledgeHubBuilderEditPage: {
    path: '/space/:spaceId/chatbot/knowledgehub/builder/:questionSetId',
    requiresAuthentication: true,
    requiresRoles: [Role.CHATBOT_ADMIN_ROLE]
  },
  CorporateDictionaryPage: {
    path: '/space/:spaceId/chatbot/dictionary',
    requiresAuthentication: true,
    requiresRoles: [Role.CHATBOT_ADMIN_ROLE]
  },
  CorporateDictionaryBulkPage: {
    path: '/space/:spaceId/chatbot/dictionary/bulk',
    requiresAuthentication: true,
    requiresRoles: [Role.CHATBOT_ADMIN_ROLE]
  },
  ChatbotAnalyticsPage: {
    path: '/space/:spaceId/chatbot/analytics',
    requiresAuthentication: true,
    requiresRoles: [Role.CHATBOT_ADMIN_ROLE]
  },
  SpaceChatbotTestPage: {
    path: '/space/:spaceId/chatbot/testyourbot',
    requiresAuthentication: true,
    requiresRoles: [Role.CHATBOT_ADMIN_ROLE]
  },
  SpaceChatbotTestBatchPage: {
    path: '/space/:spaceId/chatbot/testyourbot/:batchId/:testType',
    requiresAuthentication: true,
    requiresRoles: [Role.CHATBOT_ADMIN_ROLE]
  },
  SpaceMembersPage: {
    path: '/space/:spaceId/members',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  /* Space Members -> Role */
  SpaceMembersRolesPage: {
    path: '/space/:spaceId/members/roles',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  /* Space Members -> Role -> New user */
  SpaceMemberNewRolePage: {
    path: '/space/:spaceId/members/roles/new',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  SpaceMemberNewPage: {
    path: '/space/:spaceId/members/new',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  },
  SpaceMemberEditPage: {
    path: '/space/:spaceId/members/:userId',
    requiresAuthentication: true,
    requiresRoles: [Role.SPACE_ADMIN_ROLE]
  }
}

const routes: AppRoute[] = [
  // Cognito callbacks for authentication
  { path: '/signin', component: AuthenticationHandler, exact: true, requiresAuthentication: false },
  { path: '/signout', component: AuthenticationHandler, exact: true, requiresAuthentication: false },
  // routes when a user clicks the sign-in or sign-out buttons
  { path: '/login', component: AuthenticationHandler, exact: true, requiresAuthentication: false },
  { path: '/logout', component: AuthenticationHandler, exact: true, requiresAuthentication: false },
  // authenticated page routes
  { path: '/', component: HomePage, exact: true, requiresAuthentication: true },
  {
    component: OrganizationSettingsPage,
    exact: true,
    ...pageRoutes.OrganizationSettingsPage
  },
  {
    component: OrganizationSecurityPage,
    exact: true,
    ...pageRoutes.OrganizationSecurityPage
  },
  {
    component: OrganizationAuditPage,
    exact: true,
    ...pageRoutes.OrganizationAuditPage
  },
  {
    component: OrganizationAuditConnectorPage,
    exact: true,
    ...pageRoutes.OrganizationAuditConnectorPage
  },
  {
    component: OrganizationClientConfigsPage,
    exact: true,
    ...pageRoutes.OrganizationClientConfigsPage
  },
  {
    component: OrganizationSpacesPage,
    exact: true,
    ...pageRoutes.OrganizationSpacesPage
  },
  {
    component: OrganizationSpaceEditPage,
    exact: true,
    ...pageRoutes.OrganizationSpaceCreatePage
  },
  {
    component: OrganizationSpaceEditPage,
    exact: true,
    ...pageRoutes.OrganizationSpaceEditPage
  },
  {
    component: SpacePlaygroundPage,
    exact: true,
    ...pageRoutes.SpacePlaygroundPage
  },
  {
    component: SpaceNotificatorPage,
    exact: true,
    ...pageRoutes.SpaceNotificatorPage
  },
  {
    component: OrganizationToolbarsPage,
    exact: true,
    ...pageRoutes.OrganizationToolbarsPage
  },
  {
    component: OrganizationChannelConnectionsPage,
    exact: true,
    ...pageRoutes.OrganizationChannelConnectionsPage
  },
  {
    component: OrganizationUsersPage,
    exact: true,
    ...pageRoutes.OrganizationUsersPage
  },
  {
    component: OrganizationUserEditPage,
    exact: true,
    ...pageRoutes.OrganizationUserCreatePage
  },
  {
    component: OrganizationUserEditPage,
    exact: true,
    ...pageRoutes.OrganizationUserEditPage
  },
  {
    component: OrganizationUserBulkAddPage,
    exact: true,
    ...pageRoutes.OrganizationUserBulkAddPage
  },
  {
    component: OrganizationUserAttributesPage,
    exact: true,
    ...pageRoutes.OrganizationUserAttributesPage
  },
  {
    component: OrganizationUserAttributeCreatePage,
    exact: true,
    ...pageRoutes.OrganizationUserAttributeCreatePage
  },
  {
    component: OrganizationUserAttributeEditPage,
    exact: true,
    ...pageRoutes.OrganizationUserAttributeEditPage
  },
  {
    component: OrganizationGroupsPage,
    exact: true,
    ...pageRoutes.OrganizationGroupsPage
  },
  {
    component: OrganizationGroupsMembersPage,
    exact: true,
    ...pageRoutes.OrganizationGroupsMembersPage
  },
  {
    component: OrganizationUserProvisioningPage,
    exact: true,
    ...pageRoutes.OrganizationUserProvisioningPage
  },
  {
    component: SpaceNotificationSourcesPage,
    exact: true,
    ...pageRoutes.SpaceNotificationSourcesPage
  },
  {
    component: SpacePublisherPage,
    exact: true,
    ...pageRoutes.SpacePublisherPage
  },
  {
    component: SpacePublisherBuilderPage,
    exact: true,
    ...pageRoutes.SpacePublisherBuilderPage
  },
  {
    component: SpacePublisherEditPage,
    exact: true,
    ...pageRoutes.SpacePublisherEditPage
  },
  {
    component: SpacePublisherClonePage,
    exact: true,
    ...pageRoutes.SpacePublisherClonePage
  },
  {
    component: SpacePublisherLogsPage,
    exact: true,
    ...pageRoutes.SpacePublisherLogsPage
  },
  {
    component: SpaceAnalyticsPage,
    exact: true,
    ...pageRoutes.SpaceAnalyticsPage
  },
  {
    component: ({ match }: { match: { params: { spaceId: string } } }) => (
      <Redirect to={`/space/${match.params.spaceId}/apps/installed`} />
    ),
    exact: true,
    ...pageRoutes.SpaceAppsRedirect
  },
  {
    component: SpaceAppsV3InstalledPage,
    exact: true,
    ...pageRoutes.SpaceAppsV3InstalledPage
  },
  {
    component: SpaceCustomAppEditPage,
    exact: true,
    ...pageRoutes.SpaceCustomAppPage
  },
  {
    component: SpaceCustomAppEditPage,
    exact: true,
    ...pageRoutes.SpaceCustomAppEditPage
  },
  {
    component: SpaceAppCatalogV3Page,
    exact: true,
    ...pageRoutes.SpaceAppCatalogV3Page
  },
  {
    component: SpaceCatalogAppDetailsPage,
    exact: true,
    ...pageRoutes.SpaceCatalogAppDetailsEditPage
  },
  {
    component: SpaceCatalogAppDetailsPage,
    exact: true,
    ...pageRoutes.SpaceCatalogAppDetailsInstallPage
  },
  {
    component: SpaceAppCatalogV3CatalogItemPage,
    exact: true,
    ...pageRoutes.SpaceAppCatalogV3CatalogItemPage
  },
  {
    component: SpaceConnectorLogsPage,
    exact: true,
    ...pageRoutes.SpaceConnectorLogsPage
  },
  {
    component: SpaceAppBuilderPage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderPage
  },
  {
    component: SpaceAppBuilderCreateAppPage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderCreateAppPage
  },
  {
    component: SpaceAppBuilderTemplateDetailPage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderTemplateDetailPage
  },
  {
    component: SpaceAppBuilderTemplatePreviewPage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderTemplatePreviewPage
  },
  {
    component: SpaceAppBuilderImportTemplatePage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderImportTemplatePage
  },
  {
    component: SpaceAppBuilderFeaturesPage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderPreviewFeaturesPage
  },
  {
    component: SpaceAppBuilderDataSourcesPage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderDataSourcesPage
  },
  {
    component: SpaceAppBuilderDataSourcesBuilderPage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderDataSourcesBuilderPage
  },
  {
    component: SpaceAppBuilderWorkgridDataSourceDetailsPage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderWorkgridDataSourceDetailsPage
  },
  {
    component: SpaceKnowledgePage,
    exact: true,
    ...pageRoutes.SpaceKnowledgePage
  },
  {
    component: SpaceKnowledgePage,
    exact: true,
    ...pageRoutes.SpaceKnowledgeDocumentsPage
  },
  {
    component: SpaceKnowledgePage,
    exact: true,
    ...pageRoutes.SpaceKnowledgeDocumentEditPage
  },
  {
    component: SpaceKnowledgeQnASummaryPage,
    exact: true,
    ...pageRoutes.SpaceKnowledgeQnASummaryPage
  },
  {
    component: SpaceKnowledgeQnADetailsPage,
    exact: true,
    ...pageRoutes.SpaceKnowledgeQnADetailsPage
  },
  {
    component: SpaceKnowledgeQnADetailsPage,
    exact: true,
    ...pageRoutes.SpaceKnowledgeQnADetailsEditPage
  },
  {
    component: SpaceAppBuilderDetailsPage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderDetailsPage
  },
  {
    component: SpaceAppBuilderFeaturesPage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderFeaturesPage
  },
  {
    component: SpaceAppBuilderFeaturesHistoryPage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderFeaturesHistoryPage
  },
  {
    component: SpaceAppBuilderFeaturesHistoryDetailsPage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderFeaturesHistoryDetailsPage
  },
  {
    component: SpaceAppBuilderFeaturesHistoryDetailsPage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderFeaturesHistoryDetailsByDatePage
  },
  {
    component: SpaceAppBuilderAnalyticsPage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderAnalyticsPage
  },
  {
    component: SpaceAppBuilderLogsPage,
    exact: true,
    ...pageRoutes.SpaceAppBuilderLogsPage
  },
  {
    component: SpaceChatbotPage,
    exact: true,
    ...pageRoutes.SpaceChatbotPage
  },
  {
    component: SpaceChatbotLogsPage,
    exact: true,
    ...pageRoutes.SpaceChatbotLogsPage
  },
  {
    component: KnowledgeHubBuilderPage,
    exact: true,
    ...pageRoutes.KnowledgeHubBuilderPage
  },
  {
    component: KnowledgeHubBuilderPage,
    exact: true,
    ...pageRoutes.KnowledgeHubBuilderEditPage
  },
  {
    component: CorporateDictionaryPage,
    exact: true,
    ...pageRoutes.CorporateDictionaryPage
  },
  {
    component: CorporateDictionaryBulkPage,
    exact: true,
    ...pageRoutes.CorporateDictionaryBulkPage
  },
  {
    component: ChatbotAnalyticsPage,
    exact: true,
    ...pageRoutes.ChatbotAnalyticsPage
  },
  {
    component: SpaceChatbotTestPage,
    exact: true,
    ...pageRoutes.SpaceChatbotTestPage
  },
  {
    component: SpaceChatbotTestPage,
    exact: true,
    ...pageRoutes.SpaceChatbotTestBatchPage
  },
  {
    component: SpaceMembersPage,
    exact: true,
    ...pageRoutes.SpaceMembersPage
  },
  /* Space Members -> Role */
  {
    component: SpaceMembersRolesPage,
    exact: true,
    ...pageRoutes.SpaceMembersRolesPage
  },
  /* Space Members -> Role -> New user */
  {
    component: SpaceMemberEditPage,
    exact: true,
    ...pageRoutes.SpaceMemberNewRolePage
  },
  {
    component: SpaceMemberEditPage,
    exact: true,
    ...pageRoutes.SpaceMemberNewPage
  },
  {
    component: SpaceMemberEditPage,
    exact: true,
    ...pageRoutes.SpaceMemberEditPage
  }
]
export default routes
